import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { fetchApi } from '../services/apiBase';
import { useUserStore } from '../stores';
import ForkModal from '../components/plans/ForkModal';
import { storeForkIntent } from '../components/plans/ForkGateModal';
import { forkPlan, getCreatorCollections, CollectionSummary } from '../services/plansApi';
import { forkItinerary } from '../services/plannerApi';
import { trackEvent, trackPlanForked } from '../hooks/useAnalytics';
import { BottomNav } from '../components/navigation';
import './CreatorProfile.css';

/**
 * A creator-page card describes its own object: a public itinerary of the
 * creator's, or a published CommunityPlan. Ids collide across the two kinds.
 * An older API answers only itineraries and sends no `kind`.
 */
type CreatorPlanKind = 'itinerary' | 'community';

interface CreatorPlan {
  kind?: CreatorPlanKind;
  id: number;
  title: string;
  slug: string;
  destination: string;
  duration_days: number | null;
  group_type: string;
  /** Real uses of this card's plan (public, live copies), either kind; 0 renders nothing. */
  fork_count: number;
  lifecycle_state: string | null;
  highlight_count: number;
}

const planKind = (p: CreatorPlan): CreatorPlanKind => p.kind ?? 'itinerary';

interface CreatorData {
  username: string;
  display_name: string;
  initials: string;
  bio: string;
  destinations: string[];
  total_plans: number;
  total_forks: number;
  avg_forks_per_plan: number;
  plans: CreatorPlan[];
  joined_year: number;
}

const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

// Mounted only through `creatorRoutes.tsx`, which reads `username` off `/@<username>`.
const CreatorProfile: React.FC<{ username: string }> = ({ username }) => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [data, setData] = useState<CreatorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forkTarget, setForkTarget] = useState<CreatorPlan | null>(null);
  const [collections, setCollections] = useState<CollectionSummary[]>([]);

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    fetchApi<CreatorData>(`/auth/creators/${username}/`)
      .then(setData)
      .catch(() => setError('Creator not found'))
      .finally(() => setLoading(false));
    getCreatorCollections(username).then(d => setCollections(d.collections || [])).catch(() => {});
  }, [username]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [username]);

  const handleFork = (plan: CreatorPlan) => {
    if (!user) {
      if (plan.slug) storeForkIntent(plan.slug);
      navigate('/login', { state: { returnTo: window.location.pathname } });
      return;
    }
    setForkTarget(plan);
  };

  const handleForkConfirm = async (startDate: string): Promise<{ id: number }> => {
    if (!forkTarget) throw new Error('No plan');
    if (planKind(forkTarget) === 'itinerary') {
      // A public itinerary forks by id (the public-by-id path); the event
      // carries the SOURCE itinerary, as the public page's does.
      const result = await forkItinerary(forkTarget.id, startDate);
      trackEvent('plan_forked', {
        itinerary_id: forkTarget.id,
        destination: forkTarget.destination || '',
        source: 'creator_page',
      });
      return { id: result.id };
    }
    const result = await forkPlan(forkTarget.id, startDate);
    trackPlanForked(forkTarget.id, forkTarget.destination);
    return { id: (result as { id: number }).id };
  };

  if (loading) {
    return (
      <div className="cp-page">
        <div className="cp-loading">Loading profile...</div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="cp-page">
        <div className="cp-error">{error || 'Not found'}</div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  return (
    <div className="cp-page">
      {/* Header */}
      <div className="cp-header">
        <button className="cp-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
        </button>
        <span className="cp-url-badge">vizday.com/@{data.username}</span>
      </div>

      {/* Avatar + name */}
      <div className="cp-identity">
        <div className="cp-avatar">{data.initials}</div>
        <h1 className="cp-name">{data.display_name}</h1>
        <p className="cp-username">@{data.username}</p>
        {data.bio && <p className="cp-bio">{data.bio}</p>}
      </div>

      {/* Destination tags */}
      {data.destinations.length > 0 && (
        <div className="cp-dest-tags">
          {data.destinations.map(d => (
            <span key={d} className="cp-dest-tag">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="var(--color-sage)" stroke="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
              {d}
            </span>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="cp-stats">
        <div className="cp-stat">
          <span className="cp-stat-num">{data.total_plans}</span>
          <span className="cp-stat-label">Plans shared</span>
        </div>
        {/* A use stat is hidden when the integer it would print is 0 (the
            average is rounded by the server, so it can be 0 while the total is not). */}
        {data.total_forks > 0 && (
          <div className="cp-stat">
            <span className="cp-stat-num">{fmt(data.total_forks)}</span>
            <span className="cp-stat-label">Times used</span>
          </div>
        )}
        {data.avg_forks_per_plan > 0 && (
          <div className="cp-stat">
            <span className="cp-stat-num">{fmt(data.avg_forks_per_plan)}</span>
            <span className="cp-stat-label">Avg uses/plan</span>
          </div>
        )}
      </div>

      {/* Collections */}
      {collections.length > 0 && (
        <div className="cp-plans-section" style={{ marginBottom: 24 }}>
          <div className="cp-plans-header">
            <span className="cp-plans-label">Collections</span>
            <span className="cp-plans-count">{collections.length}</span>
          </div>
          {collections.map(c => (
            <div key={c.id} className="cp-plan-card" onClick={() => navigate(`/@${data.username}/collections/${c.slug}`)}>
              <div className="cp-plan-info">
                <span className="cp-plan-title">{c.title}</span>
                <span className="cp-plan-meta">{c.description || `${c.plan_count} plans`}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Plans */}
      <div className="cp-plans-section">
        <div className="cp-plans-header">
          <span className="cp-plans-label">Plans by {data.display_name}</span>
          <span className="cp-plans-count">{data.plans.length} plans</span>
        </div>

        {data.plans.length === 0 && (
          <p className="cp-empty">No public plans yet.</p>
        )}

        {data.plans.map(p => (
          <div key={`${planKind(p)}-${p.id}`} className="cp-plan-card" onClick={() => navigate(`/plans/p/${p.slug}`)}>
            <div className="cp-plan-info">
              <span className="cp-plan-title">{p.title}</span>
              <span className="cp-plan-meta">
                {/* Joined from parts, for both card kinds: no dangling separator,
                    and no use segment at 0. */}
                {[
                  p.destination,
                  p.duration_days ? `${p.duration_days} days` : '',
                  p.fork_count > 0
                    ? (p.fork_count === 1 ? '1 time used' : `${p.fork_count} times used`)
                    : '',
                ].filter(Boolean).join(' · ')}
              </span>
              <div className="cp-plan-footer">
                {p.fork_count > 0 && (
                  <span className="cp-plan-forks">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 3v6M18 3v6M6 9c0 3 6 3 6 6v3M18 9c0 3-6 3-6 6"/><circle cx="6" cy="3" r="2"/><circle cx="18" cy="3" r="2"/><circle cx="12" cy="21" r="2"/></svg>
                    {fmt(p.fork_count)}
                  </span>
                )}
                {p.highlight_count > 0 && (
                  <span className="cp-plan-highlights">❤️ {p.highlight_count}</span>
                )}
                <button className="cp-plan-fork-btn" onClick={e => { e.stopPropagation(); handleFork(p); }}>
                  Make it mine
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="cp-joined">Member since {data.joined_year}</p>

      {/* Fork modal */}
      {forkTarget && (
        <ForkModal
          planTitle={forkTarget.title}
          destination={forkTarget.destination}
          creatorName={data.display_name}
          durationDays={forkTarget.duration_days || 3}
          onConfirm={handleForkConfirm}
          onGoToPlan={(id) => navigate(`/itinerary/${id}?chat=1`)}
          onSharePlan={() => { setForkTarget(null); }}
          onClose={() => setForkTarget(null)}
        />
      )}

      <BottomNav activeItem="explore" />
    </div>
  );
};

export default CreatorProfile;
